/**
 * Клієнт до Pages Functions (/api/*).
 *
 * Усе спілкування з D1 йде через functions/. Клієнт не знає про SQL — він
 * просто кидає JSON і отримує JSON. Усі помилки нормалізуються в ApiError,
 * щоб UI міг показати зрозуміле повідомлення, а не стек.
 */

const BASE = '/api'

export class ApiError extends Error {
  constructor(message, { status = 0, payload = null } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }
}

async function request(path, { method = 'GET', body, query, signal, timeoutMs = 30_000 } = {}) {
  const url = new URL(BASE + path, window.location.origin)
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '' || v === 'all') continue
      url.searchParams.set(k, String(v))
    }
  }

  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(new Error('timeout')), timeoutMs)
  if (signal) signal.addEventListener('abort', () => ctrl.abort(signal.reason), { once: true })

  let res
  try {
    res = await fetch(url, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal: ctrl.signal,
    })
  } catch {
    clearTimeout(timer)
    if (ctrl.signal.aborted) {
      throw new ApiError(signal?.aborted ? 'Запит скасовано' : 'Час очікування минув', { status: 0 })
    }
    throw new ApiError('Немає зв’язку з сервером. Перевірте, чи запущено D1.', { status: 0 })
  }
  clearTimeout(timer)

  const text = await res.text()
  let payload = null
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = { error: text.slice(0, 300) }
    }
  }

  if (!res.ok || payload?.ok === false) {
    throw new ApiError(payload?.error ?? `Помилка ${res.status}`, {
      status: res.status,
      payload,
    })
  }

  return payload
}

const get = (path, query, opts) => request(path, { ...opts, method: 'GET', query })
const post = (path, body, opts) => request(path, { ...opts, method: 'POST', body })
const put = (path, body, opts) => request(path, { ...opts, method: 'PUT', body })
const del = (path, query, opts) => request(path, { ...opts, method: 'DELETE', query })

// ── Картки ───────────────────────────────────────────────────────────────────
export const api = {
  // Картки
  listCards: (query) => get('/cards', query).then((r) => r.cards),
  createCard: (card) => post('/cards', card),
  importCards: (payload) => post('/cards', payload),
  updateCard: (patch) => put('/cards', patch),
  deleteCard: (id) => del('/cards', { id }),
  suspendCard: (id, suspended) => put('/cards', { id, suspended }),

  // Повторення
  dueCards: (query) => get('/reviews', query).then((r) => r.cards),
  gradeCard: (payload) => post('/reviews', payload),
  resetCard: (cardId) => del('/reviews', { cardId }),

  // Колоды
  listDecks: () => get('/decks').then((r) => r.decks),
  createDeck: (deck) => post('/decks', deck),
  updateDeck: (deck) => put('/decks', deck),
  deleteDeck: (id) => del('/decks', { id }),

  // Теги
  listTags: (query) => get('/tags', query).then((r) => r.tags),
  createTags: (names) => post('/tags', { names }),

  // Конспекти
  listNotes: (query) => get('/notes', query).then((r) => r.notes),
  getNote: (id) => get('/notes', { id }).then((r) => r.note),
  createNote: (note) => post('/notes', note),
  updateNote: (note) => put('/notes', note),
  deleteNote: (id) => del('/notes', { id }),

  // Статистика
  stats: () => get('/stats'),

  // Файли
  uploadFile: (file) => post('/files', { name: file.name, dataUrl: file.dataUrl }),

  // Генерація (окремий метод, бо потрібен довший таймаут)
  generate: (payload, { signal, onProgress } = {}) =>
    request('/generate', { method: 'POST', body: payload, signal, timeoutMs: 180_000, onProgress }),
}

// ── Утиліти для файлів ──────────────────────────────────────────────────────

/** File → data URL (для Gemini та для картинок у картках). */
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new ApiError('Не вдалося прочитати файл'))
    reader.readAsDataURL(file)
  })
}

/** Читає лише перші `maxBytes` байт — щоб показати прев'ю до завантаження. */
export function fileToPreviewUrl(file, { maxBytes = 4 * 1024 * 1024 } = {}) {
  const blob = file.size > maxBytes ? file.slice(0, maxBytes) : file
  return URL.createObjectURL(blob)
}
